import React, { useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import Step1 from "./step-1";
import Step2 from "./step-2";
import { ResourceTypes } from "../../../constants/enums";
import {
  donateToCampaign,
  donateToPlatform,
  donateToProject,
} from "../../../services/donation.service";
import { getAccessToken } from "../../../services/auth/auth.storage";
import { Styles } from "./styles";

const DonationForm = ({ resolvedType = ResourceTypes.PLATFORM, id, onStatusChange, instanceId = "", isInDetailsPage = false }) => {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const isLoggedIn = !!getAccessToken();
  const allowMonthly =
    isInDetailsPage &&
    (resolvedType === ResourceTypes.PROJECT || resolvedType === ResourceTypes.CAMPAIGN);
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validateOnChange, setValidateOnChange] = useState(false);
  const [validateOnBlur, setValidateOnBlur] = useState(false);

  const step1Schema = useMemo(
    () =>
      Yup.object().shape({
        amount: Yup.string()
          .required(t("Please select or enter an amount."))
          .test("positive", t("Please select or enter an amount."), (v) => {
            const n = Number(v);
            return Number.isFinite(n) && n > 0;
          }),
        donationType: Yup.string()
          .oneOf(["one-time", "monthly"], t("Invalid donation type."))
          .required(),
      }),
    [t]
  );

  const step2Schema = useMemo(
    () =>
      Yup.object().shape({
        firstName: Yup.string().required(t("This field is required.")),
        lastName: Yup.string().required(t("This field is required.")),
        email: Yup.string().required(t("This field is required.")).email(t("Invalid email address.")),
        isAnonymous: Yup.boolean(),
      }),
    [t]
  );

  const submitDonation = async (values) => {
    if (!id && resolvedType !== ResourceTypes.PLATFORM) {
      onStatusChange?.({ type: "error", message: t("Missing donation id.") });
      return;
    }

    const isMonthlyDonation = allowMonthly && values.donationType === "monthly";
    const payload = {
      amount: Number(values.amount),
      isAnonymous: !!values.isAnonymous,
      notes: (values.notes || "").trim(),
      firstName: (values.firstName || "").trim(),
      lastName: (values.lastName || "").trim(),
      email: (values.email || "").trim(),
    };

    setIsSubmitting(true);
    onStatusChange?.({ type: "loading", message: "" });

    try {
      let response;

      if (isMonthlyDonation) {
        const monthlyPayload = {
          amount: payload.amount,
          isAnonymous: payload.isAnonymous,
          notes: payload.notes,
        };
        response =
          resolvedType === ResourceTypes.PROJECT
            ? await donateToProject({ ...monthlyPayload, projectId: id }, true)
            : await donateToCampaign({ ...monthlyPayload, campaignId: id }, true);
      } else if (resolvedType === ResourceTypes.PROJECT) {
        response = await donateToProject({ ...payload, projectId: id });
      } else if (resolvedType === ResourceTypes.PLATFORM) {
        response = await donateToPlatform(payload, false);
      } else {
        response = await donateToCampaign({
          ...payload,
          campaignId: id,
          subscribeToUpdates: values.subscribeToUpdates ?? false,
        });
      }

      if (response?.status >= 200 && response?.status < 300) {
        const checkoutUrl = response?.data?.data?.checkoutUrl;
        if (checkoutUrl) {
          window.location.assign(checkoutUrl);
          return;
        }
        onStatusChange?.({ type: "success", message: t("Donation submitted successfully.") });
        setStep(1);
        return true;
      } else {
        onStatusChange?.({ type: "error", message: t("Donation failed. Please try again.") });
      }
    } catch (error) {
      onStatusChange?.({ type: "error", message: t("Donation failed. Please try again.") });
      return false;
    } finally {
      setIsSubmitting(false);
    }
    return false;
  };

  const formik = useFormik({
    initialValues: {
      amount: "",
      donationType: "one-time",
      firstName: "",
      lastName: "",
      email: "",
      isAnonymous: false,
    },
    validationSchema: step === 1 ? step1Schema : step2Schema,
    validateOnChange,
    validateOnBlur,
    onSubmit: async (submittedValues, { setErrors, resetForm }) => {
      if (step === 1) {
        try {
          await step1Schema.validate(submittedValues, { abortEarly: false });
          setStep(2);
        } catch (err) {
          if (err.name === "ValidationError" && err.inner) {
            const byField = err.inner.reduce((acc, e) => {
              if (e.path) acc[e.path] = e.message;
              return acc;
            }, {});
            setErrors(byField);
          }
        }
        return;
      }
      const success = await submitDonation(submittedValues);
      if (success) resetForm();
    },
  });

  const handleStep1Next = () => {
    formik.handleSubmit();
  };

  const handleStep2Back = () => {
    setStep(1);
  };

  if (step === 1) {
    return (
      <Styles>
        <Step1
          formik={formik}
          onNext={handleStep1Next}
          isAr={isAr}
          isInDetailsPage={isInDetailsPage}
          isLoggedIn={isLoggedIn}
          showMonthlyOption={allowMonthly}
        />
      </Styles>
    );
  }

  const handleEnableValidation = () => {
    setValidateOnChange(true);
    setValidateOnBlur(true);
  };

  return (
    <Styles>
      <Step2
        formik={formik}
        onBack={handleStep2Back}
        isSubmitting={isSubmitting}
        isAr={isAr}
        instanceId={instanceId}
        onEnableValidation={handleEnableValidation}
        isInDetailsPage={isInDetailsPage}
        isLoggedIn={isLoggedIn}
      />
    </Styles>
  );
};

export default DonationForm;
